'use client'

import { useEffect, useState } from 'react'
import { LogIn, LogOut, Settings, Shield, UserCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

type User = {
  id: number
  email: string
  handle: string | null
  avatarPath: string | null
  isAdmin?: boolean
}

export function AuthBadge({ mobile = false }: { mobile?: boolean }) {
  const [user, setUser] = useState<User | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setUser(data?.user ?? null)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function handleLogout() {
    await fetch('/api/logout', { method: 'POST' }).catch(() => {})
    window.location.reload()
  }

  if (!loaded) return null

  if (!user) {
    if (mobile) {
      return (
        <a
          href="/login"
          className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <LogIn className="h-4 w-4" />
          Log in
        </a>
      )
    }
    return (
      <Button variant="outline" size="sm" nativeButton={false} render={<a href="/login" />}>
        <LogIn className="h-4 w-4" />
        Log in
      </Button>
    )
  }

  if (mobile) {
    return (
      <>
        <a
          href="/account"
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          {user.avatarPath ? (
            <img src={user.avatarPath} alt="" className="h-5 w-5 rounded-full object-cover" />
          ) : (
            <UserCircle2 className="h-5 w-5 text-primary" />
          )}
          {user.handle || 'My account'}
        </a>
        {user.isAdmin && (
          <a
            href="/admin"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <Shield className="h-4 w-4" />
            Admin panel
          </a>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <a
        href="/account"
        title="Your account"
        className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card/60 transition-colors hover:border-primary/40"
      >
        {user.avatarPath ? (
          <img
            src={user.avatarPath}
            alt={user.handle || 'Your avatar'}
            className="h-7 w-7 rounded-full object-cover"
          />
        ) : (
          <UserCircle2 className="h-5 w-5 text-primary" />
        )}
      </a>
      {user.isAdmin && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Admin panel"
          title="Admin panel"
          nativeButton={false}
          render={<a href="/admin" />}
        >
          <Shield className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Account settings"
        title="Change handle & avatar"
        nativeButton={false}
        render={<a href="/account" />}
      >
        <Settings className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Log out" title="Log out" onClick={handleLogout}>
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  )
}
